<template>
  <!-- Devtool Panel (development only) -->
  <DevtoolPanel v-if="isDev && DevtoolPanel" />

  <!-- Devtool Toggle Button (development only) -->
  <!-- <button
    v-if="isDev && devtool"
    @click="devtool.toggleVisibility"
    class="fixed lg:bottom-4 bottom-16 right-4 z-40 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
    title="Open Developer Tools"
  >
    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  </button> -->
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref } from 'vue'
import { Capacitor } from '@capacitor/core'

// Motion plugin is not currently configured, so we'll use alternative mobile shortcuts
const Motion: any = null

// Devtool imports (development only)
let DevtoolPanel: any = null
let useDevtool: any = null

if (import.meta.env.DEV) {
  DevtoolPanel = (await import('@/components/DevtoolPanel.vue')).default
  useDevtool = (await import('@/stores/devtool')).useDevtool
}
const devtool = import.meta.env.DEV ? useDevtool?.() : null
const isDev = import.meta.env.DEV

// Reactive mobile shortcut state
const shortcutState = reactive({
  tapCount: 0,
  tapTimer: null as NodeJS.Timeout | null,
  lastTapTime: 0,
  shakeThreshold: 15, // Adjust sensitivity
  lastShakeTime: 0,
  shakeCooldown: 2000, // 2 seconds between shake detections
  longPressTimer: null as NodeJS.Timeout | null,
  isLongPressing: false
})

// Keyboard shortcut handler
function handleKeydown(event: KeyboardEvent) {
  // Cmd + . (or Ctrl + . on Windows/Linux) - Toggle devtools
  if ((event.metaKey || event.ctrlKey) && event.key === '.') {
    event.preventDefault()
    if (devtool) {
      devtool.toggleVisibility()
    }
  }

  // Escape key - Close devtools if open
  if (event.key === 'Escape') {
    if (devtool && devtool.isVisible) {
      event.preventDefault()
      devtool.toggleVisibility()
    }
  }
}

// Triple tap handler
function handleTripleTap(event: TouchEvent) {
  const now = Date.now()

  if (now - shortcutState.lastTapTime < 500) {
    // Within 500ms of last tap
    shortcutState.tapCount++
  } else {
    shortcutState.tapCount = 1
  }

  shortcutState.lastTapTime = now

  if (shortcutState.tapCount === 3) {
    event.preventDefault()
    if (devtool) {
      devtool.toggleVisibility()
    }
    shortcutState.tapCount = 0
  }

  // Reset tap count after 1 second
  if (shortcutState.tapTimer) {
    clearTimeout(shortcutState.tapTimer)
  }
  shortcutState.tapTimer = setTimeout(() => {
    shortcutState.tapCount = 0
  }, 1000)
}

// Shake detection handler (using device orientation as fallback)
function handleShake(event: any) {
  const now = Date.now()

  // Prevent multiple shake detections within cooldown period
  if (now - shortcutState.lastShakeTime < shortcutState.shakeCooldown) {
    return
  }

  const { x, y, z } = event.accelerationIncludingGravity || event
  const acceleration = Math.sqrt(x * x + y * y + z * z)

  if (acceleration > shortcutState.shakeThreshold) {
    shortcutState.lastShakeTime = now
    if (devtool) {
      devtool.toggleVisibility()
    }
  }
}

// Fallback shake detection using device orientation
function handleOrientationChange(event: DeviceOrientationEvent) {
  const now = Date.now()

  if (now - shortcutState.lastShakeTime < shortcutState.shakeCooldown) {
    return
  }

  const { alpha, beta, gamma } = event
  if (alpha !== null && beta !== null && gamma !== null) {
    const acceleration = Math.sqrt(alpha * alpha + beta * beta + gamma * gamma)

    if (acceleration > shortcutState.shakeThreshold) {
      shortcutState.lastShakeTime = now
      if (devtool) {
        devtool.toggleVisibility()
      }
    }
  }
}

// Long press handler (alternative mobile shortcut)
// function handleTouchStart(event: TouchEvent) {
//   if (event.touches.length === 1) {
//     shortcutState.isLongPressing = true
//     shortcutState.longPressTimer = setTimeout(() => {
//       if (shortcutState.isLongPressing && devtool) {
//         devtool.toggleVisibility()
//         // Add haptic feedback if available
//         if (Capacitor.isNativePlatform()) {
//           // You could add haptic feedback here
//         }
//       }
//     }, 1000) // 1 second long press
//   }
// }

// function handleTouchEnd() {
//   shortcutState.isLongPressing = false
//   if (shortcutState.longPressTimer) {
//     clearTimeout(shortcutState.longPressTimer)
//     shortcutState.longPressTimer = null
//   }
// }

// function handleTouchMove() {
//   // Cancel long press if user moves finger
//   shortcutState.isLongPressing = false
//   if (shortcutState.longPressTimer) {
//     clearTimeout(shortcutState.longPressTimer)
//     shortcutState.longPressTimer = null
//   }
// }

onMounted(() => {
  if (!isDev || !devtool) return

  // Keyboard shortcut
  document.addEventListener('keydown', handleKeydown)

  // Mobile shortcuts
  if (Capacitor.isNativePlatform()) {
    // Shake detection (if Motion plugin is available)
    if (Motion) {
      Motion.addListener('accel', handleShake)
    } else {
      // Fallback shake detection using device orientation
      window.addEventListener('deviceorientation', handleOrientationChange, true)
    }

    // Touch events for triple tap and long press
    // document.addEventListener('touchstart', handleTouchStart, { passive: true })
    // document.addEventListener('touchend', handleTouchEnd, { passive: true })
    // document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleTripleTap, { passive: true })

    console.log(
      'Devtools shortcuts available: Cmd+. (or Ctrl+.) | Esc | Triple tap | Shake device'
    )
  } else {
    // Web fallback - just keyboard shortcuts
    console.log('Devtools shortcuts available: Cmd+. (or Ctrl+.) | Esc')
  }
})

onUnmounted(() => {
  if (!isDev) return

  // Clean up event listeners
  document.removeEventListener('keydown', handleKeydown)

  if (Capacitor.isNativePlatform()) {
    if (Motion) {
      Motion.removeAllListeners()
    } else {
      window.removeEventListener('deviceorientation', handleOrientationChange, true)
    }
    // document.removeEventListener('touchstart', handleTouchStart)
    // document.removeEventListener('touchend', handleTouchEnd)
    // document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTripleTap)
  }
})
</script>
